import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { storage } from '@abyss/web/tools/storage';
import { V2Alert } from '@abyss/web/ui/Alert';
import { IconMaterial } from '@abyss/web/ui/IconMaterial';
import { Layout } from '@abyss/web/ui/Layout';
import { Text } from '@abyss/web/ui/Text';
import find from 'lodash/find';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSessionStorage } from 'usehooks-ts';

import { useLagoon } from '../../../../..//hooks/useLagoon';
import { adobeLinkTrackEvent } from '../../../../../common/AdobeTagging/adobeLinkTrackEvent';
import { Constants, LOB } from '../../../../../common/Constants';
import { ConstantsLagoon } from '../../../../../common/ConstantsLagoon';
import {
  hideScrollbar,
  mediumScreenOnly,
} from '../../../../../common/ConstantsStyles';
import { MapDisplay } from '../../../../../common/MapDisplay';
import { getGeoLocationFromStorage } from '../../../../../common/PSXHeader/SearchBar/utils';
import { SelectLocationCard } from '../../../../../common/SelectLocationCard';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { useUserLocation } from '../../../../../hooks/useUserLocation';
import { ProviderLocation } from '../../../../../models/ProviderDetails';
import { Directions } from '../../../../../models/RouteDirections';
import { handleMapClickLocation } from '../../../../../utils/directions.utils';
import {
  getRoute,
  mapViewUpdatePin,
  setResultIcon,
  setZoomLevel,
  updateIconSize,
} from '../../../../../utils/map.utils';
import { getSelectedLocationIndex } from '../../../../../utils/providerDetails.utils';
import { getCurrentMember } from '../../../../../utils/user.utils';
import { useConfig } from '../../../context/Analytics';
import { Popup } from '../../MapView/Popup';
import { getEnableMapPinRedesign } from '../../MapView/utils';
import {
  ChooseLocationCardsContainer,
  ChooseLocationLabel,
  ChooseLocationRow,
  LocationsWrapColumn,
  MapViewWrap,
  MobileMapViewWrap,
  MobileViewMapButton,
  NewPatientDisclaimer,
  SidePaneCloseButton,
} from './ChoosePCPLocation.styled';
import { PCPLocationAlertHeaderCss } from './ConfirmPCPLocation.styled';

type Props = {
  hasMultipleLocations: boolean;
  isMobileMapView: boolean;
  mobileScreen: boolean;
  providerLocations: ProviderLocation[];
  saveAndCancelButtons: React.ReactNode;
  setIsMobileMapView: (isMobileMapView: boolean) => void;
  useStickyButtonRow: boolean;
  setSelectedAddressIndex: (selectedAddressIndex: number) => void;
  handleBackButtonClick: () => void;
  setMobileRouteView: (isMobileMapView: boolean) => void;
  mobileRouteView: boolean;
  map: any;
  routeEndCoords?: [number | null, number | null];
  setRouteEndCoords(coords: [number | null, number | null]): void;
};

export const LocationsMapViewWrap = ({
  hasMultipleLocations,
  isMobileMapView,
  mobileScreen,
  providerLocations,
  saveAndCancelButtons,
  setIsMobileMapView,
  setSelectedAddressIndex,
  useStickyButtonRow,
  handleBackButtonClick,
  setMobileRouteView,
  mobileRouteView,
  map,
  routeEndCoords,
  setRouteEndCoords,
}: Props) => {
  const currentMember = getCurrentMember();

  const shouldShowLocation = (location: ProviderLocation) => {
    const networksArray = Array.isArray(location.networks)
      ? location.networks
      : [location.networks];
    const networkAffils = networksArray.flatMap(
      (n) => n?.networkAffiliations ?? []
    );
    const hasNetworkAffiliations = networkAffils.length > 0;

    if (
      hasNetworkAffiliations &&
      (currentMember?.lineOfBusiness === LOB.MNR ||
        currentMember?.isUspNiceMember)
    ) {
      const validNetworkAffiliations = networkAffils.some(
        (na) =>
          na.acceptingNewPatients &&
          (na.pcpIndicator === true || na.pcpIndicator?.toString() === 'true')
      );

      if (!validNetworkAffiliations) {
        return location.acceptingNewPatients;
      }

      return validNetworkAffiliations;
    }

    return location.acceptingNewPatients;
  };
  const [showMapCollapse, enableMapPinRedesignFlag] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.SHOW_MAP_COLLAPSE,
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_PIN_REDESIGN,
  ]);

  const [enableMapEnhancements] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_LIST_VIEW_MAP_ENHANCEMENTS,
  ]);

  const mapOnPremKey = useConfig('MAPBOX_ONPREM_KEY');
  const mapOnPremURL = useConfig('MAPBOX_ONPREM_URL');

  const [, getUserLocation] = useUserLocation({});

  const [closeSidePanel, setCloseSidePanel] = useState<boolean>(false);
  const [, setDoPopupUpdate] = useState<boolean>(false);
  const [popupContent, setPopupContent] = useState<any>('');
  const [directions, setDirections] = useState<Directions>({
    userPlaceName: '',
    endPlaceName: '',
    routes: [],
  });
  const mediumScreen = useMediaQuery(mediumScreenOnly);
  const { t } = useTranslation();
  const { longitude, latitude } = getGeoLocationFromStorage();
  const [highlightId, setHighlightId] = useSessionStorage<any>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_HIGHLIGHT_ID,
    { providerId: '', from: 'mapView' }
  );
  const [selectedId, setSelectedId] = useSessionStorage<string | null>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    null
  );
  const [currentSelectedId, setCurrentSelectedId] = useSessionStorage<
    string | null
  >(Constants.STORAGE_KEYS.SESSION.PROVIDER_MAP_PIN_SELECTED_ID, null);

  const [mapPinCoords] = useSessionStorage(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_COORDS,
    []
  );

  const lagoonData = useLagoon('ui-messaging')();

  const newPatientDisclaimer = find(lagoonData, {
    key: ConstantsLagoon.CHOOSE_PCP_FLOW.NEW_PATIENT_DISCLAIMER,
  });

  const bannerContent = find(lagoonData, {
    key: ConstantsLagoon.PRIMARY_CARE.CHOOSE_PCP_LOCATION_BANNER,
  });

  const [showAlert, setShowAlert] = useState<boolean>(true);

  const highlightRefs = React.useRef<any>({});
  const mobileMapViewClassName = isMobileMapView
    ? 'location-card-map-view'
    : '';

  useEffect(() => {
    const selectedLocationIndex = getSelectedLocationIndex(
      providerLocations,
      currentSelectedId
    );
    setSelectedId(providerLocations[selectedLocationIndex].locationId);
    setCurrentSelectedId(providerLocations[selectedLocationIndex].locationId);
  }, []);

  useEffect(() => {
    highlightRefs.current[highlightId.providerId]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start',
    });
  }, [highlightId]);

  useEffect(() => {
    map.current?.resize();
    if (!closeSidePanel) {
      setZoomLevel(map.current, mapPinCoords);
    }
  }, [closeSidePanel]);

  useEffect(() => {
    const fetchRouteDirections = async () => {
      const { longitude: locationLng, latitude: locationLat } =
        providerLocations?.[0];
      const routeDirections = await getRoute(
        map.current,
        longitude,
        latitude,
        +locationLng,
        +locationLat,
        getUserLocation,
        mediumScreen,
        'driving-traffic',
        0,
        mapOnPremKey,
        mapOnPremURL
      );
      if (routeDirections) setDirections(routeDirections);
      setRouteEndCoords([+locationLng, +locationLat]);
    };
    if (map.current) {
      map.current.on('load', () => {
        fetchRouteDirections();
      });
    }
  }, [map.current]);

  useEffect(() => {
    if (mobileScreen && !routeEndCoords?.[0] && mobileRouteView) {
      setMobileRouteView(false);
      setIsMobileMapView(false);
      handleBackButtonClick();
    }
  }, [routeEndCoords, mobileScreen]);

  const handleMapButton = () => {
    setIsMobileMapView(true);
  };

  const selectLocation = (index, location) => {
    setSelectedId(location.locationId);
    setSelectedAddressIndex(index);
  };
  const handleDirectionClick = async (location) => {
    const { longitude: locationLng, latitude: locationLat } = location;
    const routeDirections = await getRoute(
      map.current,
      longitude,
      latitude,
      +locationLng,
      +locationLat,
      getUserLocation,
      mediumScreen,
      'driving-traffic',
      0,
      mapOnPremKey,
      mapOnPremURL
    );
    if (routeDirections) {
      setDirections(routeDirections);
    }

    setRouteEndCoords([locationLng, locationLat]);
    if (!isMobileMapView && mobileScreen) {
      setIsMobileMapView(true);
      setMobileRouteView(true);
    }
  };

  const handleLocationCardMouseEnter = async (
    index,
    location,
    flyToPin = false
  ) => {
    if (mobileScreen) return;
    await setZoomLevel(map.current, mapPinCoords);

    if (enableMapEnhancements) {
      const marker = document.querySelector(
        `#custom-marker-${location.locationId}`
      );
      updateIconSize(true, marker, true);
    }
    setResultIcon(
      map.current,
      index,
      true,
      setHighlightId,
      location.locationId
    );
    if (flyToPin) {
      map.current?.flyTo({
        center: [+location.longitude, +location.latitude],
        zoom: Constants.DEVICE_LOCATION.ZOOM,
      });
    }
    mapViewUpdatePin(
      {
        providerId: location.locationId,
        address: getProviderLocation(location.locationId, providerLocations)
          ?.address,
        providerName: getProviderName(location.locationId, providerLocations),
        latitude: location.latitude,
        longitude: location.longitude,
      },
      flyToPin,
      popupContent,
      setPopupContent,
      map
    );
  };

  const handleLocationCardMouseLeave = (index, location) => {
    if (enableMapEnhancements) {
      const marker = document.querySelector(
        `#custom-marker-${location.locationId}`
      );
      updateIconSize(false, marker, true);
    }
    if (!selectedId) {
      setResultIcon(map.current, index, false, setHighlightId, null);
    }
  };

  const handleClickLocation = async (locationId, locationLng, locationLat) => {
    await handleMapClickLocation(
      map.current,
      selectedId,
      longitude,
      latitude,
      locationId,
      locationLng,
      locationLat,
      mapOnPremKey,
      mapOnPremURL,
      mobileScreen,
      setDirections,
      setRouteEndCoords,
      setSelectedId,
      getUserLocation
    );
  };

  const forceMapPinRedesign = storage.session.get(
    Constants.STORAGE_KEYS.SESSION.FORCE_MAP_PIN_REDESIGN
  );

  const enableMapPinRedesign: boolean = getEnableMapPinRedesign(
    enableMapPinRedesignFlag,
    forceMapPinRedesign
  );

  const updatePin = (practitioner, flyToPin = false) => {
    if (enableMapPinRedesign) {
      if (!practitioner) {
        setDoPopupUpdate(false);
        mapViewUpdatePin(null, false, popupContent, setPopupContent, map);
        return;
      }

      setDoPopupUpdate(true);
      return;
    }

    if (mobileScreen) {
      setPopupContent({ ...practitioner });
      return;
    }
    mapViewUpdatePin(
      practitioner,
      flyToPin,
      popupContent,
      setPopupContent,
      map
    );
  };

  const wrapperSelectedStyle = {
    border: '3px solid #002677',
    borderRadius: '12px',
  };

  const chooseALocationHeading =
    hasMultipleLocations && !isMobileMapView ? (
      <ChooseLocationRow
        data-auto-testid="choose-a-location-row"
        data-testid="choose-a-location-row"
      >
        <ChooseLocationLabel
          color="$primary1"
          display="h4"
          fontWeight="$bold"
          offset={1}
        >
          {t('CHOOSE_PCP_FLOW.CHOOSE_A_LOCATION')}
        </ChooseLocationLabel>
        <MobileViewMapButton
          before={
            <IconMaterial
              color="$interactive1"
              icon="map"
              isScreenReadable
              size="15px"
            />
          }
          data-auto-testid="map-button"
          data-testid="map-button"
          onClick={handleMapButton}
          variant="ghost"
        >
          {t('View Map')}
        </MobileViewMapButton>
      </ChooseLocationRow>
    ) : null;

  const getProviderName = (providerId, locations) => {
    const providerLocation = locations.find(
      (location) => location.locationId === providerId
    );
    if (providerLocation) {
      return providerLocation.providerGroupsAffiliations?.[0]?.value;
    }
    return '';
  };

  const getProviderLocation = (providerId, locations) =>
    locations.find((location) => location.locationId === providerId);

  return (
    <>
      <MobileMapViewWrap
        className={isMobileMapView ? 'show-mobile-map-view' : ''}
        cssProps={{ mobileRouteView }}
      >
        <LocationsWrapColumn
          className="locations-wrap-column"
          css={hideScrollbar}
        >
          {(!closeSidePanel || mobileScreen) && !mobileRouteView ? (
            <Layout.Stack alignItems="left" grow space={12}>
              {chooseALocationHeading}
              <ChooseLocationCardsContainer
                aria-label="Choose a location"
                className="choose-location-container"
                role="group"
              >
                {bannerContent && showAlert ? (
                  <V2Alert
                    children={bannerContent?.message || ''}
                    css={PCPLocationAlertHeaderCss}
                    data-testid="choose-pcp-location-alert"
                    dismissible={true}
                    isVisible={true}
                    onClose={() => {
                      setShowAlert(false);
                    }}
                    status="info"
                    title={bannerContent?.title || ''}
                  />
                ) : null}
                {providerLocations?.map(
                  (location, index) =>
                    shouldShowLocation(location) && (
                      <div
                        className="location-cards-container"
                        data-auto-testid="choose-pcp-location-section"
                        data-testid="choose-pcp-location-section"
                        key={`pcp-location-${index + 1}`}
                        onBlur={() =>
                          handleLocationCardMouseLeave(index, location)
                        }
                        onFocus={() =>
                          handleLocationCardMouseEnter(index, location)
                        }
                        onMouseEnter={() =>
                          handleLocationCardMouseEnter(index, location)
                        }
                        onMouseLeave={() =>
                          handleLocationCardMouseLeave(index, location)
                        }
                        style={
                          selectedId === location.locationId
                            ? wrapperSelectedStyle
                            : {}
                        }
                      >
                        <SelectLocationCard
                          className={`
                      ${mobileMapViewClassName} ${
                            highlightId.providerId === location.locationId
                              ? 'highlight'
                              : ''
                          }
                    `}
                          handleDirectionClick={() =>
                            handleDirectionClick(location)
                          }
                          highlightRefs={highlightRefs}
                          index={index}
                          isMobileMapView={isMobileMapView}
                          location={location}
                          selectLocation={() => selectLocation(index, location)}
                          selected={selectedId === location.locationId}
                          selectedId={selectedId}
                          setIsMobileMapView={setIsMobileMapView}
                          showBadgeRow
                          totalLocations={providerLocations.length}
                        />
                      </div>
                    )
                )}
              </ChooseLocationCardsContainer>

              {!isMobileMapView ? (
                <NewPatientDisclaimer
                  data-auto-testid="patient-disclaimer"
                  data-testid="patient-disclaimer"
                >
                  <Text fontWeight="$medium" size={12.64}>
                    {newPatientDisclaimer?.message ?? ''}
                  </Text>
                </NewPatientDisclaimer>
              ) : null}
              {!useStickyButtonRow ? saveAndCancelButtons : null}
            </Layout.Stack>
          ) : null}
          {showMapCollapse ? (
            <SidePaneCloseButton
              alignItems="center"
              className="side-btn"
              justify="center"
            >
              <IconMaterial
                css={{
                  fill: '$black',
                }}
                data-auto-testid="sidepanel-close-icon"
                data-testid="sidepanel-close-icon"
                icon={
                  closeSidePanel
                    ? 'keyboard_arrow_right'
                    : 'keyboard_arrow_left'
                }
                onClick={() => {
                  adobeLinkTrackEvent({
                    name: `sidepanel:${closeSidePanel ? 'open' : 'close'}`,
                    location: 'body:map',
                  });
                  setCloseSidePanel(!closeSidePanel);
                }}
                size="$md"
              />
            </SidePaneCloseButton>
          ) : null}
        </LocationsWrapColumn>
        <MapViewWrap
          cssProps={{ closeSidePanel, mobileRouteView, enableMapEnhancements }}
        >
          {providerLocations?.length ? (
            <MapDisplay
              coords={{ longitude, latitude }}
              data-testid="choose-pcp-map"
              directions={directions}
              locationResults={providerLocations}
              map={map}
              mobileScreen={mobileScreen}
              onClick={() => {
                adobeLinkTrackEvent({
                  name: `sidepanel:${closeSidePanel ? 'open' : 'close'}`,
                  location: 'body:map',
                });
                setCloseSidePanel(!closeSidePanel);
              }}
              refToBeFocusedOnDirectionsClose={
                highlightRefs.current[highlightId.providerId] as HTMLElement
              }
              routeEndCoords={routeEndCoords}
              selectLocation={handleClickLocation}
              setPopupContent={setPopupContent}
              setRouteEndCoords={setRouteEndCoords}
              shouldResetSelectedId={false}
              skipNavigationFocus
              updatePin={updatePin}
            />
          ) : null}
        </MapViewWrap>
      </MobileMapViewWrap>
      {!mobileScreen && (
        <React.Fragment>
          <Popup
            handleClickLocation={handleClickLocation}
            index={getSelectedLocationIndex(
              providerLocations,
              highlightId.providerId
            )}
            isChooseLocation={true}
            practitioner={{
              providerId: highlightId.providerId,
              address: getProviderLocation(
                highlightId.providerId,
                providerLocations
              )?.address,
              providerName: getProviderName(
                highlightId.providerId,
                providerLocations
              ),
            }}
            selectLocation={() =>
              selectLocation(
                getSelectedLocationIndex(
                  providerLocations,
                  highlightId.providerId
                ),
                getProviderLocation(highlightId.providerId, providerLocations)
              )
            }
            selected={selectedId === highlightId.providerId}
            totalLocations={providerLocations.length}
            updatePin={updatePin}
          />
        </React.Fragment>
      )}
    </>
  );
};
